import type Phaser from "phaser";

/** 마스크 직사각형 — world-space 픽셀, 좌상단 기준. */
export interface MaskRect {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

/**
 * 직사각형 영역으로 게임 오브젝트를 클리핑하는 GeometryMask 핸들.
 *
 * Phaser는 Group/Container에 setMask를 걸어도 이후 추가되는 동적 자식에 전파하지 않는다.
 * 따라서 매 인스턴스 생성 시점에 `apply(obj)`를 호출해 같은 마스크를 적용한다 — 하나의
 * GeometryMask를 공유하므로 `setRect()`로 영역을 갱신하면 적용된 모든 오브젝트에 즉시 반영된다.
 *
 * 사용법:
 * ```ts
 * const mask = new RectMask(scene, { x, y, width, height });
 * mask.apply(brick);        // 오브젝트가 생성될 때마다
 * mask.apply(projectile);
 * mask.setRect(newRect);    // 리사이즈 시 (선택)
 * mask.destroy();           // scene shutdown 시
 * ```
 *
 * (ScrollableList의 contentMask와 동일한 마스크 패턴을 도메인 무관 헬퍼로 추출.)
 */
export class RectMask {
  private readonly gfx: Phaser.GameObjects.Graphics;
  private readonly geoMask: Phaser.Display.Masks.GeometryMask;
  private rect: MaskRect;

  constructor(scene: Phaser.Scene, rect: MaskRect) {
    this.rect = rect;
    // display list 밖의 Graphics — 형상만 GeometryMask에 제공(직접 렌더되지 않음).
    this.gfx = scene.make.graphics({});
    this.redraw();
    this.geoMask = this.gfx.createGeometryMask();
  }

  /** 동적으로 추가되는 게임 오브젝트에 마스크 적용. 체이닝용으로 입력 오브젝트를 그대로 반환. */
  apply<T extends Phaser.GameObjects.GameObject>(obj: T): T {
    (obj as unknown as Phaser.GameObjects.Components.Mask).setMask(this.geoMask);
    return obj;
  }

  /** 마스크 직사각형 갱신(리사이즈/레이아웃 변경). 동일 GeometryMask 참조라 적용된 오브젝트에 자동 반영. */
  setRect(rect: MaskRect): void {
    this.rect = rect;
    this.redraw();
  }

  /** GeometryMask 직접 노출 — 직접 setMask를 호출하는 소비자용(ScrollableList.contentMask와 동형). */
  get mask(): Phaser.Display.Masks.GeometryMask {
    return this.geoMask;
  }

  /**
   * 현재 마스크 직사각형(읽기 전용) — 펼침 축소 등 setRect 갱신이 반영된 최신 영역.
   * 호출자가 "오브젝트가 이 영역에 완전히 포함되는가"를 판정해 불필요한 개별 마스크를 생략하는 데 쓴다
   * (모바일 stencil 비용 절감 — 영역 안 객체는 클립이 무의미). 영역에 걸치거나 밖인 객체만 마스크하면 된다.
   */
  get bounds(): MaskRect {
    return this.rect;
  }

  /** scene shutdown 시 Graphics 정리(ScrollableList.preDestroy와 동형). */
  destroy(): void {
    this.gfx.destroy();
  }

  private redraw(): void {
    this.gfx.clear();
    this.gfx.fillStyle(0xffffff);
    this.gfx.fillRect(this.rect.x, this.rect.y, this.rect.width, this.rect.height);
  }
}

/** RectMask 팩토리 — `new RectMask(...)`와 동일. */
export function createRectMask(scene: Phaser.Scene, rect: MaskRect): RectMask {
  return new RectMask(scene, rect);
}
